import { Fragment, useEffect, useState } from 'react';
import { Box, Grid, Radio, Typography } from '@mui/material';
import SectionTitle from '../components/SectionTitle';
import GridItem from '../components/GridItem';
import { Ti18nId } from 'hooks/useI18n';
import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
  onRequired: (id: Ti18nId) => void;
  onSuccess: (message: string) => void;
}

const radioIds = [
  'skin',
  'lanugo',
  'plantar_surface',
  'breast',
  'eye_ear',
  'genitals_male',
  'genitals_female',
] as const;

type RadioId = typeof radioIds[number];
type Option = { value: number; label: string };
type FieldDef = { name: RadioId; title: string; options: Option[] };

const FIELDS: FieldDef[] = [
  {
    name: 'skin',
    title: '피부',
    options: [
      { value: -1, label: '끈적이고 손상되기 쉽고 투명함' },
      { value: 0, label: '끈적끈적하다.\n붉고 반투명하다.' },
      { value: 1, label: '부드러운 분홍색 정맥이 보인다.' },
      { value: 2, label: '표피 벗겨짐,\n홍반, 정맥 약간 보임.' },
      { value: 3, label: '갈라지고 부위가 창백하다. 정맥이 드물게 보인다.' },
      { value: 4, label: '벗겨지거나 깊게 갈라진다. 혈관이 보이지 않는다.' },
      { value: 5, label: '축 쳐진다. 피부가 갈라진다. 주름진다.' },
    ],
  },
  {
    name: 'lanugo',
    title: '솜털',
    options: [
      { value: -1, label: '없음' },
      { value: 0, label: '약간 분포' },
      { value: 1, label: '풍성함' },
      { value: 2, label: '가늘고 드문드문' },
      { value: 3, label: '벗겨진 영역 있음' },
      { value: 4, label: '대부분 없음' },
      { value: 5, label: '' },
    ],
  },
  {
    name: 'plantar_surface',
    title: '발바닥 주름',
    options: [
      { value: -1, label: '발꿈치-발가락 40-50mm: -1\n<40mm: -2' },
      { value: 0, label: '>50mm에서 발금없음' },
      { value: 1, label: '희미한 붉은선' },
      { value: 2, label: '앞쪽에만 가로주름' },
      { value: 3, label: '앞쪽 2/3 주름' },
      { value: 4, label: '발바닥 전체 주름' },
      { value: 5, label: '' },
    ],
  },
  {
    name: 'breast',
    title: '가슴',
    options: [
      { value: -1, label: '형태를 알 수 없음' },
      { value: 0, label: '겨우 알 수 있음' },
      { value: 1, label: '편평한 유륜\n몽우리 없음' },
      { value: 2, label: '점같은 유륜, 몽우리 1–2mm' },
      { value: 3, label: '솟은 유륜, 몽우리 3–4mm' },
      { value: 4, label: '완전한 유륜 몽우리 5–10mm' },
      { value: 5, label: '' },
    ],
  },
  {
    name: 'eye_ear',
    title: '눈/귀',
    options: [
      { value: -1, label: '눈꺼풀 붙어있는 정도가\n미미: -1 / 명확: -2' },
      {
        value: 0,
        label: '눈꺼풀이 떨어짐\n귓바퀴가 편평,\n접었을때 접힌 상태로 있음',
      },
      { value: 1, label: '귓바퀴 부드럽고 접은 후 서서히 펴짐' },
      { value: 2, label: '귓바퀴 부드럽고, 접은 후 바로 펴지려함' },
      { value: 3, label: '귓바퀴가 잘 형성되고 견고하며, 접은 후 즉시 펴짐' },
      { value: 4, label: '두꺼운 연골, 귀가 단단' },
      { value: 5, label: '' },
    ],
  },
  {
    name: 'genitals_male',
    title: '생식기 (남)',
    options: [
      { value: -1, label: '음낭 편평·부드러움' },
      { value: 0, label: '음낭 비어있고 주름이 없음' },
      { value: 1, label: '고환 서혜관에 있고, 주름이 거의 없음' },
      { value: 2, label: '고환 하강해 있고, 주름 적음' },
      { value: 3, label: '고환 하강, 주름 적당' },
      { value: 4, label: '고환 늘어짐, 주름 깊음' },
      { value: 5, label: '' },
    ],
  },
  {
    name: 'genitals_female',
    title: '생식기 (여)',
    options: [
      { value: -1, label: '음핵 돌출, 음순 편평' },
      { value: 0, label: '음핵 돌출, 작은 소음순' },
      { value: 1, label: '음핵 돌출, 큰 소음순' },
      { value: 2, label: '대·소음순 같이 돌출' },
      { value: 3, label: '큰 대음순, 작은 소음순' },
      { value: 4, label: '대음순이 음핵·소음순 완전 덮음' },
      { value: 5, label: '' },
    ],
  },
];

const Physical = (props: Props) => {
  const { disabled, getValues, setValue, register } = props;

  // 폼에 필드 등록(마운트 시 1회)
  useEffect(() => {
    radioIds.forEach(key => register(key));
  }, [register]);

  // 합계 계산
  const calcSum = (): number =>
    radioIds.reduce((acc, key) => {
      const n = Number(getValues(key));
      return Number.isFinite(n) ? acc + n : acc;
    }, 0);

  const [sumValue, setSumValue] = useState<number>(0);

  // 초기 합계
  useEffect(() => {
    setSumValue(calcSum());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // RHF 값 갱신 + 즉시 합계 갱신
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;
    setValue(name as RadioId, Number(value), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setSumValue(calcSum());
  };

  // 완전 제어 라디오 셀
  const CellWithRadio = ({
    name,
    value,
    label,
  }: {
    name: RadioId;
    value: number;
    label: string;
  }) => {
    const current = Number(getValues(name));
    return (
      <Grid item xs={1}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '120px',
            justifyContent: 'center',
            whiteSpace: 'pre-wrap',
          }}
        >
          <Radio
            name={name}
            value={String(value)} // 문자열로 주는 것이 안전
            checked={current === value} // 제어형
            onChange={handleChange}
            disabled={disabled}
          />
          <Typography
            variant="caption"
            textAlign="center"
            sx={{ fontSize: '14px', lineHeight: 1.2, mt: 0.5 }}
          >
            {label}
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderHeader = () => (
    <>
      <Grid item xs={1} sx={{ width: '120px' }}>
        <Box sx={{ width: '300px' }}>
          <GridItem bgColor="#0000001F" text="" />
        </Box>
      </Grid>
      {[-1, 0, 1, 2, 3, 4, 5].map(n => (
        <Grid item xs={1} sx={{ width: '120px' }} key={`h-${n}`}>
          <Box sx={{ width: '300px' }}>
            <GridItem bgColor="#0000001F" text={`${n}`} />
          </Box>
        </Grid>
      ))}
    </>
  );

  const renderRow = (field: FieldDef) => (
    <Fragment key={field.name}>
      <Grid item xs={1} sx={{ width: '120px' }}>
        <Box sx={{ width: '300px' }}>
          <GridItem bgColor="#0000001F" text={field.title} />
        </Box>
      </Grid>
      {field.options.map((opt, idx) => (
        <CellWithRadio
          key={`${field.name}-${idx}`}
          name={field.name}
          value={opt.value}
          label={opt.label}
        />
      ))}
    </Fragment>
  );

  return (
    <Fragment>
      <SectionTitle title="신체성숙도 사정 (Physical Maturity)" />
      <Box sx={{ width: '90%', margin: '36px auto' }}>
        <Grid container spacing={2} columns={8}>
          {renderHeader()}
          {FIELDS.map(renderRow)}
        </Grid>

        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            sx={{ mt: '20px' }}
          >
            <Typography
              gutterBottom
              minWidth={115}
              fontWeight={700}
              variant="subtitle1"
            >
              합계: {sumValue}점
            </Typography>
          </Box>
        </Grid>
      </Box>
    </Fragment>
  );
};

export default Physical;
